/**
 * Header balance pill — same UX pattern as secretary-pwa's AppShell.
 *
 * Polls /api/v1/billing/balance every 60s, displays the current rupee
 * balance with a click-to-recharge affordance. Refreshes immediately when
 * the user successfully completes a recharge (via the global event).
 */

import { useEffect, useState } from 'react';
import { Wallet } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { billingApi, readBalance, currencySymbol } from '@/lib/billing';

interface BalancePillProps {
  onClickRecharge: () => void;
}

export function BalancePill({ onClickRecharge }: BalancePillProps) {
  const qc = useQueryClient();
  const { data, isError } = useQuery({
    queryKey: ['billing-balance'],
    queryFn: () => billingApi.getBalance(),
    refetchInterval: 60_000,
    retry: false,
  });

  // Listen for "credits refreshed" events from the recharge sheet
  useEffect(() => {
    const handler = () => qc.invalidateQueries({ queryKey: ['billing-balance'] });
    window.addEventListener('axondesktop.billing.refreshed', handler);
    return () =>
      window.removeEventListener('axondesktop.billing.refreshed', handler);
  }, [qc]);

  // Always render the pill — placeholder while loading / on error.
  const hasData = !!data && !isError;
  const credits = hasData ? readBalance(data) : 0;
  const symbol = hasData ? currencySymbol(data) : '₹';
  const depleted = hasData && credits <= 0;
  const low = hasData && credits > 0 && credits <= 49;

  return (
    <button
      onClick={onClickRecharge}
      className={clsx(
        'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
        !hasData
          ? 'bg-muted text-muted-foreground hover:bg-muted/80'
          : depleted
          ? 'bg-destructive/15 text-destructive hover:bg-destructive/25'
          : low
          ? 'bg-amber-500/15 text-amber-700 hover:bg-amber-500/25'
          : 'bg-primary/10 text-primary hover:bg-primary/15',
      )}
      title={
        !hasData
          ? 'Loading balance — click to recharge'
          : depleted
          ? 'Balance depleted — click to recharge'
          : low
          ? 'Low balance — click to recharge'
          : 'Click to view & recharge'
      }
    >
      <Wallet className="w-3 h-3" />
      <span>
        {symbol}
        {hasData
          ? credits.toLocaleString('en-IN', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
          : '—'}
      </span>
    </button>
  );
}
